import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CtaSection from '../../components/feature/CtaSection';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function Facilities() {
  useSEO({
    title: '施設紹介｜おかやま小児科クリニック｜岡山市北区',
    description:
      '岡山市北区のおかやま小児科クリニックの院内施設紹介。清潔で明るい待合室、最新医療機器完備。お子様に優しい快適な空間をご提供しています。',
    keywords:
      '岡山市,小児科,施設紹介,院内設備,医療機器,待合室,診察室,北区',
    canonicalPath: '/facilities',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '施設紹介',
          'クリニックの院内施設、医療設備の紹介',
          '/facilities'
        ),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[25vh] sm:h-[30vh] flex items-center justify-center overflow-hidden mt-20 bg-gradient-to-r from-[#F48FB1] via-[#D4A0D0] to-[#A78BFA]">
        <div className="relative z-10 text-center text-white px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">施設紹介</h2>
          <p className="text-base sm:text-lg md:text-xl">お子様に優しい快適な空間</p>
        </div>
      </section>

      {/* Facilities Gallery */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">院内のご案内</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              清潔で明るい、お子様が安心できる環境
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            {/* 外観 */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/27caa707b01c408bbecf991cfbc2e8a9.webp"
                  alt="外観"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">外観</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  明るく清潔感のある外観で、お子様連れでも安心してご来院いただけます。駐車場も完備しております。
                </p>
              </div>
            </div>

            {/* 受付 */}
            <div className="bg-gradient-to-br from-[#EDE7F6]/40 to-[#FCE4EC]/30 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/86d73feb05b2313cff4ed092876d13fa.webp"
                  alt="受付"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">受付</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  明るく笑顔のスタッフがお迎えいたします。初診の方も安心してご来院ください。予約や各種お手続きもスムーズに対応いたします。
                </p>
              </div>
            </div>

            {/* 待合室 */}
            <div className="bg-gradient-to-br from-[#FFFDE7]/60 to-[#FCE4EC]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/f323c5abece1d531b589faf149551c09.webp"
                  alt="待合室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">待合室</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  広々とした待合室には、お子様が退屈しないよう絵本やおもちゃをご用意しております。明るく清潔な空間で、リラックスしてお待ちいただけます。
                </p>
              </div>
            </div>

            {/* キッズスペース */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/40 to-[#EDE7F6]/30 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/cd2fa3a679d9bd87da4435f54142591b.webp"
                  alt="キッズスペース"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">キッズスペース</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  お子様が楽しく遊べるキッズスペースを完備。待ち時間も退屈せず、安全に過ごしていただけます。
                </p>
              </div>
            </div>

            {/* 診察室 */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d04b5461f67d0058688486d4ed3bba49.webp"
                  alt="診察室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">診察室</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  最新の医療機器を完備した診察室で、お子様の症状を丁寧に診察いたします。プライバシーに配慮した個室タイプです。
                </p>
              </div>
            </div>

            {/* 処置室 */}
            <div className="bg-gradient-to-br from-[#EDE7F6]/40 to-[#FCE4EC]/30 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/075dfa1824c8f6f64dfb78e80706461c.webp"
                  alt="処置室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">処置室</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  予防接種や各種処置を行う専用の処置室です。お子様の不安を和らげる工夫を施し、スタッフが優しくサポートいたします。
                </p>
              </div>
            </div>

            {/* レントゲン室 */}
            <div className="bg-gradient-to-br from-[#FFFDE7]/60 to-[#FCE4EC]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/53c42baba3a4c04e8c3ef1412fb01e5b.webp"
                  alt="レントゲン室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">レントゲン室</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  デジタルレントゲン装置を導入し、低被ばくで迅速な撮影が可能です。お子様への負担を最小限に抑えます。
                </p>
              </div>
            </div>

            {/* 授乳室 */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/40 to-[#EDE7F6]/30 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/faca585050e25f89d68cc7bcbc4b7b7c.webp"
                  alt="授乳室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">授乳室</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  プライバシーに配慮した授乳室を完備。ゆったりとした空間で、安心して授乳やおむつ替えをしていただけます。
                </p>
              </div>
            </div>

            {/* トイレ */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="h-56 sm:h-64">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8bfe0245ca7af46bc57c509009100756.webp"
                  alt="トイレ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 sm:p-6">
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">トイレ</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  お子様用の補助便座やおむつ替え台を設置。バリアフリー対応で、どなたでも快適にご利用いただけます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFFDE7]/60 via-[#FCE4EC]/40 to-[#EDE7F6]/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">医療設備</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              最新の医療機器で正確な診断
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: 'ri-microscope-line', title: '血液検査機器', desc: '迅速な検査結果で適切な治療を' },
              { icon: 'ri-heart-pulse-line', title: '心電図検査', desc: '心臓の状態を詳しく検査' },
              { icon: 'ri-lungs-line', title: '呼吸機能検査', desc: '喘息などの呼吸器疾患の診断' },
              { icon: 'ri-test-tube-line', title: '尿検査機器', desc: '腎臓や尿路の状態をチェック' },
              { icon: 'ri-thermometer-line', title: '体温測定器', desc: '非接触型で衛生的に測定' },
              { icon: 'ri-medicine-bottle-line', title: '吸入器', desc: '喘息治療に使用する吸入療法' },
            ].map((equipment, index) => (
              <div
                key={index}
                className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#F48FB1] to-[#A78BFA] rounded-full flex items-center justify-center mb-4 sm:mb-6 mx-auto">
                  <i className={`${equipment.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2 sm:mb-3 text-center">
                  {equipment.title}
                </h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                  {equipment.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />

      <Footer />
    </div>
  );
}
